import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  DocumentReference,
  DocumentSnapshot,
} from "firebase/firestore";
import AppLoadingScreen from "@/components/AppLoadingScreen";
import { firestore } from "@/firebase";
import {
  FIRESTORE_COLLECTION_NAME,
  FIRESTORE_COLLECTION_LIVESESSIONS,
  FIRESTORE_COLLECTION_STREAMS,
} from "@/config";
import { getBatchViewerLink, resolveAnalysisBatchId } from "@/utils/navigation";

// Mirrors AnalysisViewerPage's livestream lookup: new-structure sessions live
// at streams/{streamId}/sessions/{sessionId}; old-structure sessions live at
// the root liveSessions collection keyed by session id alone.
async function safeGetDoc(
  ref: DocumentReference,
): Promise<DocumentSnapshot | null> {
  try {
    return await getDoc(ref);
  } catch {
    // Unreadable (e.g. anonymous share-token visitor, or a collection the
    // rules deny) — treat as unknown rather than failing the guard.
    return null;
  }
}

type Resolution =
  | { key: string; target: "legacy" }
  | { key: string; target: "batch"; batchId: string }
  | { key: string; target: "index" };

/**
 * Route guard for the legacy `/events/:videoId(/:analysisId)` viewer
 * (`AnalysisViewerPage`).
 *
 * The legacy page is the ONLY viewer for live-stream sessions, and after this
 * guard it renders for nothing else. Decision order:
 *
 *  1. Router state marks an in-app livestream navigation
 *     (`pendingLivestreamStart` for a brand-new session whose Firestore doc
 *     may not exist yet — never race a lookup against it — or
 *     `livestreamSession` for existing sessions) → render the legacy page.
 *  2. `?batchId=` in the URL → instant redirect to the batch viewer.
 *  3. Otherwise resolve via Firestore (one parallel round-trip):
 *     analysis doc carries a batch id → batch viewer; the video doc is a
 *     livestream (`is_livestream`) or a stream/liveSessions session doc
 *     exists for these ids → render the legacy live viewer; anything else →
 *     analyses index.
 *  4. Nothing readable (missing doc, or an anonymous share-token visitor
 *     whose Firestore reads are denied) → analyses index. The standalone
 *     Rapid Review viewer is retired and must never be a fallback.
 *
 * Batch redirects preserve the `/live-demo` prefix and the full query string
 * (share `token`, `t`, `sweep`, search context, ...). Retired analysis links
 * return to the matching analyses index.
 */
export default function LegacyAnalysisRedirect({
  children,
}: {
  children: ReactNode;
}) {
  const { videoId, analysisId } = useParams();
  const location = useLocation();
  const navState = location.state as {
    pendingLivestreamStart?: boolean;
    livestreamSession?: boolean;
  } | null;
  const isMarkedLivestream = Boolean(
    navState?.pendingLivestreamStart || navState?.livestreamSession,
  );

  const params = new URLSearchParams(location.search);
  const urlBatchId = params.get("batchId");
  const lookupKey = `${videoId ?? ""}/${analysisId ?? ""}`;
  const needsLookup = !isMarkedLivestream && !urlBatchId && Boolean(videoId);
  const [resolution, setResolution] = useState<Resolution | null>(null);

  useEffect(() => {
    if (!needsLookup || !videoId) return;
    let cancelled = false;
    (async () => {
      const videoRef = doc(firestore, FIRESTORE_COLLECTION_NAME, videoId);
      const analysisRef = analysisId
        ? doc(firestore, FIRESTORE_COLLECTION_NAME, videoId, "analyses", analysisId)
        : null;
      const liveRef = analysisId
        ? doc(
            firestore,
            FIRESTORE_COLLECTION_STREAMS,
            videoId,
            "sessions",
            analysisId,
          )
        : doc(firestore, FIRESTORE_COLLECTION_LIVESESSIONS, videoId);

      const [videoSnap, analysisSnap, liveSnap] = await Promise.all([
        safeGetDoc(videoRef),
        analysisRef ? safeGetDoc(analysisRef) : Promise.resolve(null),
        safeGetDoc(liveRef),
      ]);
      if (cancelled) return;

      const batchId = analysisSnap?.exists()
        ? resolveAnalysisBatchId(analysisSnap.data())
        : null;
      const isLivestream = Boolean(
        (videoSnap?.exists() &&
          (videoSnap.data() as { is_livestream?: boolean })?.is_livestream) ||
          liveSnap?.exists(),
      );

      if (batchId) {
        setResolution({ key: lookupKey, target: "batch", batchId });
      } else if (isLivestream) {
        setResolution({ key: lookupKey, target: "legacy" });
      } else {
        setResolution({ key: lookupKey, target: "index" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsLookup, lookupKey, videoId, analysisId]);

  if (isMarkedLivestream || !videoId) {
    return <>{children}</>;
  }

  const prefix = location.pathname.startsWith("/live-demo/")
    ? "/live-demo"
    : "";

  const buildBatchRedirect = (batchId: string) => {
    const target = new URLSearchParams(location.search);
    target.delete("batchId");
    const legacyEventIndex = target.get("eventIndex");
    if (legacyEventIndex !== null && !target.has("pe")) {
      target.set("pe", legacyEventIndex);
    }
    // pv= focuses this video inside the batch viewer on arrival
    target.set("pv", videoId);
    return `${prefix}${getBatchViewerLink(batchId)}?${target.toString()}`;
  };

  if (urlBatchId) {
    return <Navigate to={buildBatchRedirect(urlBatchId)} replace />;
  }

  const resolved =
    resolution && resolution.key === lookupKey ? resolution : null;

  if (!resolved) {
    return <AppLoadingScreen />;
  }

  if (resolved.target === "batch") {
    return <Navigate to={buildBatchRedirect(resolved.batchId)} replace />;
  }

  if (resolved.target === "index") {
    return <Navigate to={`${prefix}/events`} replace />;
  }

  return <>{children}</>;
}
